import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { loginUser } from "../../store/UserSlice";
import { AppDispatch } from "../../store/store";
import { loginSchema, LoginFormData } from "../../schemas/auth.schema";
import Input from "../form/Input";
import Button from "../form/Button";

interface LoginFormProps {
  onForgotPassword: () => void;
}

export default function LoginForm({ onForgotPassword }: LoginFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
    mode: "onSubmit",
  });

  const onSubmit = async (data: LoginFormData) => {
    const result = await dispatch(loginUser(data));

    if (loginUser.fulfilled.match(result) && result.payload) {
      toast.success(t("login.loginSuccess", "Logged In successfully"));

      const slugData = localStorage.getItem("SetSlug");
      if (slugData) {
        navigate(`/program/${slugData}`);
        return;
      }

      const loginData = localStorage.getItem("loginData");
      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);
          if (parsedData?.user?.isAdmin === true) {
            navigate("/admin/dashboard");
          } else {
            navigate("/bookings");
          }
        } catch (error) {
          console.error("Unable to parse login data:", error);
          navigate("/bookings");
        }
        return;
      }

      navigate("/bookings");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-5">
      {/* Header */}
      <div className="text-center">
        <Link to="/" className="inline-block">
          <img
            src="./images/bluelogo-new.png"
            alt="Logo"
            className="mx-auto mb-3 max-w-[130px] md:max-w-[150px]"
          />
        </Link>

        <h2 className="mb-2 font-gilroy text-[#263238] font-semibold text-[26px] md:text-[30px]">
          {t("login.title", "Log in to your account")}
        </h2>

        <p className="text-gray-7 text-[#78909C] font-gilroy text-[16px] md:text-[18px]">
          {t(
            "login.subtitle",
            "Welcome back! Sign in to access your account and manage your healthcare needs."
          )}
        </p>
      </div>

      {/* Email */}
      <Input
        id="email"
        label={t("login.emailLabel", "Email Address")}
        type="email"
        placeholder={t("login.emailPlaceholder", "Enter your email")}
        autoComplete="email"
        error={errors.email?.message}
        {...register("email")}
      />

      {/* Password */}
      <Input
        id="password"
        label={t("login.passwordLabel", "Password")}
        type="password"
        placeholder={t("login.passwordPlaceholder", "Enter your password")}
        autoComplete="current-password"
        showPasswordToggle
        error={errors.password?.message}
        {...register("password")}
      />

      {/* Remember Me & Forgot Password */}
      <div className="flex flex-row items-center justify-between font-gilroy-medium text-[#263238]">
        <div className="flex">
          <input
            type="checkbox"
            id="rememberMe"
            {...register("rememberMe")}
            className="peer"
          />

          <label
            htmlFor="rememberMe"
            className="flex items-center cursor-pointer"
          >
            <span className="ml-2 text-sm text-[#263238]">
              {t("login.rememberMe", "Remember me")}
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={onForgotPassword}
          className="text-sm text-[#037897] hover:underline"
        >
          {t("login.forgotPassword", "Forgot password?")}
        </button>
      </div>

      {/* Login Button */}
      <Button
        isLoading={isSubmitting}
        loadingText={t("login.loggingIn", "Logging in...")}
        className="!mt-2"
      >
        {t("login.loginBtn", "Login")}
      </Button>

      {/* Register Link */}
      <h4 className="text-center text-sm sm:text-base">
        {t("login.noAccount", "Don't have an account?")}{" "}
        <Link to="/register" className="text-[#037897] font-semibold">
          {t("login.registerLink", "Register")}
        </Link>
      </h4>
    </form>
  );
}
